import { useEffect } from "react";

import { useForm } from "../useForm";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import OrderNoteModal from "../../modals/OrderNoteModal";
import {
  convertToDefEventPara,
  orderStatusOptions,
} from "../../../shared/utility";
import AddServiceToOrderModal from "./AddServiceToOrderModal";
import OrderItemsTable from "./OrderItemsTable";
import AddMediaModal from "../../modals/AddMediaModal";
import SignatureModal from "../../modals/SignatureModal";
import { Accordion, AccordionTab } from "primereact/accordion";
import WorkOrderCheckbox from "./WorkOrderCheckbox";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { config } from "../../../constants";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import ImageGalleryModal from "./ImageGalleryModal";
import WorkOrderInput from "./WorkOrderInput";
import { classNames } from "primereact/utils";
import ButtonSpinner from "../../controls/ButtonSpinner";

const initialFValues = {
  id: 0,
  customer: { value: -1 },
  status: orderStatusOptions[0],
  work_number: "",
  start_date: new Date(),
  end_date: new Date(),
  customer_info: { email: "", notes: "" },
  notes: "",
  makes: [],
  services: [],
  medias: [],
  signature: "",
  check_heat_exchanger: 0,
  check_burners: 0,
  check_pilot_assembly: 0,
  check_venting: 0,
  check_high_limit_and_fan_control: 0,
  left_manifold_pressure_at: 0,
  check_temperature_rise: 0,
  check_carbon_monoxide_in_flue_products: 0,
  check_combustion_and_ventilation_air_supply: 0,
  check_compressor_and_fan_contactors: 0,
  check_refrigerant_charge: 0,
  check_safety_controls_set_points_and_functions: 0,
  check_condition_of_evaporator_and_condenser_coils: 0,
  check_condensate_drain_trap_and_pan: 0,
  check_circulating_air_blower: 0,
  check_belts_and_pulleys: 0,
  check_motor_amperages: 0,
  check_thermostats: 0,
  check_filter: 0,
  check_all_access_panels_to_be_secure: 0,
  check_all_electrical_connections: 0,
  check_disconnects: 0,
  check_gas_piping: 0,
  check_exhaust_fan: 0,
};

export default function CreateNewWorkOrderForm(props) {
  const {
    customers,
    isEdit,
    loading,
    showErrorModal,
    workOrder,
    services,
    actionCall,
    setEmptyModelOpen,
    addServiceToOrderCall,
    askEditOrderItem,
    askDeleteOrderItem,
    openAddNewMakeModal,
    removeImageAtIndexCall,
    uploadNewImageCall,
    updateMediaTitleCall,
    employeeOptions,
    onItemEditComplete,
    emailInvoice,
    emailWoInProgress,
  } = props;

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.customer = null;
    if (values.customer.value < 1) {
      temp.customer = "Please fill out field.";
    }

    temp.signature = null;
    if (values.status?.value === 4 && (values.signature?.length ?? 0) === 0) {
      temp.signature = "error";
      showErrorModal("Please get customer signature!");
    }

    temp.email = null;
    if (values.status.value === 4 && values.customer_info.email === "") {
      temp.email = "Please fill out customer email if possible";
      showErrorModal("Please fill out customer email.");
    }

    temp.makes = null;
    if (values.status.value === 4 && values.makes.length === 0) {
      temp.makes = "error";
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            buttonMessage="Mark Complete"
            confirmAction={() => actionCall(values)}
            message={`This WO has no makes and models, are you sure you want to mark as Complete?`}
            hideReason={true}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate
  );

  const handleSubmit = () => {
    if (validate()) {
      actionCall(values);
    }
  };

  useEffect(() => {
    if (isEdit) {
      setValues(workOrder);
    }
  }, [isEdit, workOrder]);

  const openCustomerSignature = () => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <SignatureModal
          confirmAction={(signature) =>
            setValues({ ...values, signature: signature })
          }
          setOpen={setEmptyModelOpen}
          title="Customer Signature"
          savedSignature={values.signature}
        />
      ),
    });
  };

  function deleteMediaAtIndex(indexToRemove) {
    removeImageAtIndexCall(values.medias[indexToRemove].id);
    setValues({
      ...values,
      medias: values.medias.filter((_, index) => index !== indexToRemove),
    });
  }

  const uploadNewImage = async function (data) {
    let callResult = await uploadNewImageCall(data);
    if (callResult.success) {
      setValues({
        ...values,
        medias: [...values.medias, ...callResult.data.images],
      });
    }
  };

  const openAddMediaModal = () => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <AddMediaModal
          confirmAction={(data) => uploadNewImage(data)}
          setOpen={setEmptyModelOpen}
          title="Customer Signature"
          savedMedia={{
            is_new: true,
            imageName: "",
            media: [],
            title: "",
          }}
          workOrderId={workOrder.id}
        />
      ),
    });
  };

  const openImageGalleryModal = (index = 0) => {
    console.log("openImageGalleryModal", index);
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <ImageGalleryModal 
          setOpen={setEmptyModelOpen} 
          images={values.medias} 
          initialIndex={index}
          onTitleUpdate={handleTitleUpdate}
        />
      ),
    });
  };

  const handleTitleUpdate = async (index, newTitle) => {
    if (values.medias[index] && values.medias[index].id) {
      const result = await updateMediaTitleCall(values.medias[index].id, newTitle);
      if (result.success) {
        // Update local state as well for immediate UI feedback
        const updatedMedias = [...values.medias];
        updatedMedias[index] = { ...updatedMedias[index], title: newTitle };
        setValues({ ...values, medias: updatedMedias });
      }
      return result;
    }
    return { success: false };
  };

  return (
    <div className="h-full space-y-2 pb-4">
      <div className="card-custom p-3 grid grid-cols-2 gap-4">
        <SelectMenu
          handleInputChange={handleInputChange}
          name="customer"
          options={customers}
          value={values.customer}
          title={"Customer"}
          error={errors.customer}
          disabled={isEdit}
          showLabel={true}
        />

        <div>
          <Input
            label="WO #"
            name="work_number"
            onChange={handleInputChange}
            value={values.work_number}
            labelOn={true}
            type="text"
            error={errors.work_number}
            disabled={true}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />
          {!isEdit ? (
            <p className="text-sm text-yellow-600 font-bold">
              WO # will be auto generated once created
            </p>
          ) : null}
        </div>

        <Input
          label="PO #"
          name="po_number"
          onChange={handleInputChange}
          value={values.po_number}
          labelOn={true}
          type="text"
          error={errors.po_number}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        />

        <Input
          label="Reason"
          name="reason"
          onChange={handleInputChange}
          value={values.reason}
          labelOn={true}
          type="text"
          error={errors.reason}
          disabled={isEdit}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
        />

        <SelectMenu
          handleInputChange={handleInputChange}
          name="status"
          options={orderStatusOptions}
          value={values.status}
          title={"Status"}
          error={errors.status}
          showLabel={true}
        />

        <div>
          <Input
            label="Email"
            name="email"
            onChange={(e) =>
              setValues({
                ...values,
                customer_info: {
                  ...values.customer_info,
                  email: e.target.value,
                },
              })
            }
            value={values.customer_info.email}
            labelOn={true}
            type="text"
            error={errors.email}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />
          <p className="text-sm text-yellow-600 font-bold">
            This is the email the WO will be emailed to.
          </p>
        </div>
      </div>
      {isEdit ? (
        <Accordion className="p-2">
          <AccordionTab header="Makes & Models">
            {workOrder.makes.map((singleMake) => (
              <div className="card-custom my-2 p-3 grid grid-cols-2 gap-x-4 gap-y-2">
                <Input
                  label="Make"
                  name="make"
                  onChange={handleInputChange}
                  value={singleMake.make}
                  labelOn={true}
                  type="text"
                  error={errors.make}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
                <Input
                  label="Model"
                  name="model"
                  onChange={handleInputChange}
                  value={singleMake.model}
                  labelOn={true}
                  type="text"
                  error={errors.model}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
                <Input
                  label="Type"
                  name="type"
                  onChange={handleInputChange}
                  value={singleMake.type}
                  labelOn={true}
                  type="text"
                  error={errors.type}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
                <Input
                  label="Serial"
                  name="serial"
                  onChange={handleInputChange}
                  value={singleMake.serial}
                  labelOn={true}
                  type="text"
                  error={errors.serial}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
                <div className="col-span-2 w-full flex justify-end">
                  <button
                    type="submit"
                    className="mt-4 rounded-md bg-main-purple p-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => openAddNewMakeModal(true, singleMake)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button
                type="submit"
                className="mt-4 rounded-md bg-main-purple p-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => openAddNewMakeModal(false, {})}
              >
                Add new
              </button>
            </div>
          </AccordionTab>
        </Accordion>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <div className="card-custom p-3 mt-3">
          <div className="flex justify-between">
            <p className="text-sm text-gray-700 font-semibold">
              Work Performed
            </p>
            <button
              onClick={() =>
                setEmptyModelOpen({
                  childComponent: (
                    <OrderNoteModal
                      title="Describe the work performed"
                      orderNote={values.work_performed}
                      confirmAction={(text) =>
                        handleInputChange(
                          convertToDefEventPara("work_performed", text)
                        )
                      }
                      setOpen={setEmptyModelOpen}
                    />
                  ),
                  open: true,
                })
              }
              className="text-sm font-semibold main-purple"
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-gray-700 font-normal">
            {values.work_performed ? values.work_performed : <br />}
          </p>
        </div>

        <div className="card-custom p-3 mt-3">
          <div className="flex justify-between">
            <p className="text-sm text-gray-700 font-semibold">
              Recommendations
            </p>
            <button
              onClick={() =>
                setEmptyModelOpen({
                  childComponent: (
                    <OrderNoteModal
                      title="Recommendations"
                      orderNote={values.recommendations}
                      confirmAction={(text) =>
                        handleInputChange(
                          convertToDefEventPara("recommendations", text)
                        )
                      }
                      setOpen={setEmptyModelOpen}
                    />
                  ),
                  open: true,
                })
              }
              className="text-sm font-semibold main-purple"
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-gray-700 font-normal">
            {values.recommendations ? values.recommendations : <br />}
          </p>
        </div>

        <div className="card-custom p-3 mt-3">
          <div className="flex justify-between">
            <p className="text-sm text-gray-700 font-semibold">Notes</p>
            <button
              onClick={() =>
                setEmptyModelOpen({
                  childComponent: (
                    <OrderNoteModal
                      title="Add note"
                      orderNote={values.customer_info.notes}
                      confirmAction={(newNote) =>
                        setValues({
                          ...values,
                          customer_info: {
                            ...values.customer_info,
                            notes: newNote,
                          },
                        })
                      }
                      setOpen={setEmptyModelOpen}
                    />
                  ),
                  open: true,
                })
              }
              className="text-sm font-semibold main-purple"
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-gray-700 font-normal">
            {values.customer_info.notes ? values.customer_info.notes : <br />}
          </p>
        </div>
      </div>

      <div className="card-custom p-3 py-5">
        <div className="rounded-md mb-5">
          <SelectMenu
            handleInputChange={(e) => {
              setEmptyModelOpen({
                childComponent: (
                  <AddServiceToOrderModal
                    service={e.target.value}
                    confirmAction={addServiceToOrderCall}
                    setOpen={setEmptyModelOpen}
                    isEdit={false}
                  />
                ),
                open: true,
              });
            }}
            name="service_type"
            options={services}
            value={null}
            title="Add Service Type"
            error={null}
            autoComplete="new-password"
            showLabel={true}
            parentClass="w-full"
            disabled={!isEdit}
          />
          {!isEdit ? (
            <p className="text-sm text-yellow-600 font-bold">
              Please create the WO to add service
            </p>
          ) : null}
        </div>
        <OrderItemsTable
          loading={false}
          values={values}
          askEditOrderItem={askEditOrderItem}
          askDeleteOrderItem={askDeleteOrderItem}
          onItemEditComplete={onItemEditComplete}
          employeeOptions={employeeOptions}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Column 1 */}
        <div className="flex flex-col gap-2">
          <WorkOrderCheckbox
            name="check_heat_exchanger"
            title="1. CHECK HEAT EXCHANGER"
            isChecked={Boolean(values.check_heat_exchanger)}
            onChecked={(e) =>
              setValues({ ...values, check_heat_exchanger: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_burners"
            title="2. CHECK BURNER(S)"
            isChecked={Boolean(values.check_burners)}
            onChecked={(e) =>
              setValues({ ...values, check_burners: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_pilot_assembly"
            title="3. CHECK PILOT ASSEMBLY"
            isChecked={Boolean(values.check_pilot_assembly)}
            onChecked={(e) =>
              setValues({ ...values, check_pilot_assembly: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_venting"
            title="4. CHECK VENTING"
            isChecked={Boolean(values.check_venting)}
            onChecked={(e) =>
              setValues({ ...values, check_venting: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_high_limit_and_fan_control"
            title="5. CHECK HIGH LIMIT AND FAN CONTROL"
            isChecked={Boolean(values.check_high_limit_and_fan_control)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_high_limit_and_fan_control: e.target.checked,
              })
            }
          />
          <WorkOrderInput
            name="left_manifold_pressure_at"
            title="6. LEFT MANIFOLD PRESSURE @"
            handleInputChange={handleInputChange}
            value={values.left_manifold_pressure_at}
          />
          <WorkOrderCheckbox
            name="check_temperature_rise"
            title="7. CHECK TEMPERATURE RISE"
            isChecked={Boolean(values.check_temperature_rise)}
            onChecked={(e) =>
              setValues({ ...values, check_temperature_rise: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_carbon_monoxide_in_flue_products"
            title="8. CHECK CARBON MONOXIDE IN FLUE PRODUCTS"
            isChecked={Boolean(values.check_carbon_monoxide_in_flue_products)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_carbon_monoxide_in_flue_products: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_combustion_and_ventilation_air_supply"
            title="9. CHECK COMBUSTION AND VENTILATION AIR SUPPLY"
            isChecked={Boolean(
              values.check_combustion_and_ventilation_air_supply
            )}
            onChecked={(e) =>
              setValues({
                ...values,
                check_combustion_and_ventilation_air_supply: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_compressor_and_fan_contactors"
            title="10. CHECK COMPRESSOR AND FAN CONTACTORS"
            isChecked={Boolean(values.check_compressor_and_fan_contactors)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_compressor_and_fan_contactors: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_refrigerant_charge"
            title="11. CHECK REFRIGERANT CHARGE"
            isChecked={Boolean(values.check_refrigerant_charge)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_refrigerant_charge: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_safety_controls_set_points_and_functions"
            title="12. CHECK SAFETY CONTROLS - SET POINTS & FUNCTIONS"
            isChecked={Boolean(
              values.check_safety_controls_set_points_and_functions
            )}
            onChecked={(e) =>
              setValues({
                ...values,
                check_safety_controls_set_points_and_functions:
                  e.target.checked,
              })
            }
          />
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-2">
          <WorkOrderCheckbox
            name="check_condition_of_evaporator_and_condenser_coils"
            title="13. CHECK CONDITION OF EVAPORATOR AND CONDENSER COILS"
            isChecked={Boolean(
              values.check_condition_of_evaporator_and_condenser_coils
            )}
            onChecked={(e) =>
              setValues({
                ...values,
                check_condition_of_evaporator_and_condenser_coils:
                  e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_condensate_drain_trap_and_pan"
            title="14. CHECK CONDENSATE DRAIN TRAP AND PAN"
            isChecked={Boolean(values.check_condensate_drain_trap_and_pan)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_condensate_drain_trap_and_pan: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_circulating_air_blower"
            title="15. CHECK CIRCULATING AIR BLOWER"
            isChecked={Boolean(values.check_circulating_air_blower)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_circulating_air_blower: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_belts_and_pulleys"
            title="16. CHECK BELTS & PULLEYS"
            isChecked={Boolean(values.check_belts_and_pulleys)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_belts_and_pulleys: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_motor_amperages"
            title="17. CHECK MOTOR AMPERAGES"
            isChecked={Boolean(values.check_motor_amperages)}
            onChecked={(e) =>
              setValues({ ...values, check_motor_amperages: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_thermostats"
            title="18. CHECK THERMOSTATS"
            isChecked={Boolean(values.check_thermostats)}
            onChecked={(e) =>
              setValues({ ...values, check_thermostats: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_filter"
            title="19. CHECK FILTER"
            isChecked={Boolean(values.check_filter)}
            onChecked={(e) =>
              setValues({ ...values, check_filter: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_all_access_panels_to_be_secure"
            title="20. CHECK ALL ACCESS PANELS TO BE SECURE"
            isChecked={Boolean(values.check_all_access_panels_to_be_secure)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_all_access_panels_to_be_secure: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_all_electrical_connections"
            title="21. CHECK ALL ELECTRICAL CONNECTIONS"
            isChecked={Boolean(values.check_all_electrical_connections)}
            onChecked={(e) =>
              setValues({
                ...values,
                check_all_electrical_connections: e.target.checked,
              })
            }
          />
          <WorkOrderCheckbox
            name="check_disconnects"
            title="22. CHECK DISCONNECTS"
            isChecked={Boolean(values.check_disconnects)}
            onChecked={(e) =>
              setValues({ ...values, check_disconnects: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_gas_piping"
            title="23. CHECK GAS PIPING"
            isChecked={Boolean(values.check_gas_piping)}
            onChecked={(e) =>
              setValues({ ...values, check_gas_piping: e.target.checked })
            }
          />
          <WorkOrderCheckbox
            name="check_exhaust_fan"
            title="24. CHECK EXHAUST FAN"
            isChecked={Boolean(values.check_exhaust_fan)}
            onChecked={(e) =>
              setValues({ ...values, check_exhaust_fan: e.target.checked })
            }
          />
        </div>
      </div>

      <div className="card-custom p-3 py-5 flex flex-wrap gap-4">
        {(values.medias || []).map((media, index) => {
          // const extension = media.imageUrl?.split(".").pop()?.toLowerCase();
          const isPdf = false; // extension === "pdf";

          return (
            <div key={index} className="relative flex flex-col items-center">
              {/* X Button */}
              <button
                type="button"
                onClick={() => deleteMediaAtIndex(index)} // replace with your delete handler
                className="absolute top-0 right-0 bg-red-600 text-white text-2xl rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700"
                title="Delete"
              >
                ×
              </button>

              {isPdf ? (
                <DocumentIcon
                  className="h-32 w-32 text-black-400"
                  aria-hidden="true"
                />
              ) : (
                <div>
                  <img
                    src={
                      media.is_new
                        ? media.media
                        : config.url.STORAGE_URL + media.media
                    }
                    alt={media.imageName || `media-${index}`}
                    title={media.imageName || `media-${index}`}
                    className="media-image rounded-md"
                    height={100}
                    width={100}
                    onClick={() => openImageGalleryModal(index)}
                  />
                  <p className="text-center p-1">{media.title}</p>
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() =>
            isEdit
              ? openAddMediaModal()
              : showErrorModal("Please create WO before uploading images!")
          }
          className="mt-2 px-20 py-2 border-2 border-dashed border-gray-400 text-4xl text-gray-600 rounded hover:bg-gray-100"
          title="Add"
        >
          +
        </button>
      </div>

      {isEdit ? (
        <div className="mx-2">
          <p className="text-md font-bold">Signature:</p>
          <img
            src={values.signature}
            alt="Signature"
            style={{
              display: "block",
              width: 220,
              height: "auto",
              border: "1px solid #ddd",
              borderRadius: 8,
            }}
          />
        </div>
      ) : null}

      <div className="p-12">
        <button
          type="submit"
          className="inline-flex justify-center rounded-md bg-main-purple p-4 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 float-right"
          onClick={handleSubmit}
        >
          {isEdit ? "Save" : "Create"}
        </button>
        {/* show if valid email is in email field*/}

        <button
          type="button"
          className={classNames(
            "inline-flex justify-center rounded-md mr-4 bg-gray-500 p-4 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 float-right"
          )}
          onClick={() => emailInvoice(values.id)}
          disabled={
            values.customer_info.email == null
              ? true
              : values.customer_info.email.length === 0
          }
        >
          <ButtonSpinner spin={emailWoInProgress} text="Email WO" />
        </button>
        {isEdit ? (
          <button
            type="submit"
            className="mr-5 inline-flex justify-center rounded-md bg-yellow-500 p-4 text-sm font-semibold text-white shadow-sm hover:bg-yellow-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 float-right"
            onClick={openCustomerSignature}
          >
            Signature
          </button>
        ) : null}
      </div>
    </div>
  );
}
